"use client";

import { useAppRouter } from "@/lib/hooks/useAppRouter";
import { useState, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import { supabase } from "@/lib/supabaseClient";
import { runPostAuthProvisioning } from "@/lib/postAuthProvisioning";
import type { ProductBranding } from "@accounting-app/products";
import { useTranslations } from "next-intl";
import { Notice } from "@/components/ui/Notice";
import { trackedFetch } from "@/lib/api/trackedFetch";

// Check if we're running in a Capacitor native environment
const isNative =
  typeof window !== "undefined" &&
  !!(window as any).Capacitor?.isNativePlatform?.();

/**
 * Persist Supabase tokens to native Keychain/Keystore for voice assistant access.
 * Only runs on Capacitor native platforms (iOS/Android).
 */
async function saveTokensToNativeStorage() {
  if (!isNative) return;
  try {
    const { data: { session } } = await (await import("@/lib/supabaseClient")).supabase.auth.getSession();
    if (!session) return;
    const { TokenStorage } = await import("@/lib/tokenStorage");
    await TokenStorage.save({
      accessToken: session.access_token,
      refreshToken: session.refresh_token,
      supabaseUrl: process.env.NEXT_PUBLIC_SUPABASE_URL || "",
    });
  } catch (err) {
    console.warn("[login] Failed to save tokens to native storage:", err);
  }
}

const capacitorPlatform =
  typeof window !== "undefined"
    ? (window as any).Capacitor?.getPlatform?.()
    : undefined;

const isAndroid = capacitorPlatform === "android";
const isIOS = capacitorPlatform === "ios";

type AuthMode = "signin" | "signup";
type SignInMethod = "password" | "otp";
type OtpStep = "idle" | "sent" | "verifying";

/** Returns true if the input looks like a phone number */
function looksLikePhone(value: string): boolean {
  const stripped = value.replace(/[\s\-().+]/g, "");
  return /^\d{7,15}$/.test(stripped) || /^\+\d{7,15}$/.test(value.replace(/[\s\-()]/g, ""));
}

function AppleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  );
}

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

type LoginFormProps = {
  branding: ProductBranding;
  oauthProviders: Array<"google" | "apple">;
  allowSignUp: boolean;
};

export default function LoginForm({ branding, oauthProviders, allowSignUp }: LoginFormProps) {
  // Demo: the "Sign in with a one-time code instead" toggle is hidden, so
  // signInMethod stays "password" and the OTP screens are unreachable.
  const SHOW_OTP_SIGN_IN = false;
  // oauthProviders is resolved per-domain from the `oauth_providers` system
  // setting (empty when unconfigured), so it is the whole gate for the buttons.
  const appleOAuthEnabled = oauthProviders.includes("apple");
  const googleOAuthEnabled = oauthProviders.includes("google");
  const t = useTranslations("login");
  const router = useAppRouter();
  const searchParams = useSearchParams();
  const isAddAccount = searchParams.get("add_account") === "true";
  const prefillEmail = searchParams.get("email") ?? "";
  const oauthError = searchParams.get("error");
  const linkApple = searchParams.get("link_apple") === "true";
  const phoneParam = searchParams.get("phone");
  const igLinkParam = searchParams.get("ig_link");
  const demoTokenParam = searchParams.get("demo_token");

  // If ?phone= or ?ig_link= param is present, default to signup mode so user creates an account
  const [authMode, setAuthMode] = useState<AuthMode>(phoneParam || igLinkParam ? "signup" : "signin");
  const [phone, setPhone] = useState(phoneParam ?? "");

  const [identifier, setIdentifier] = useState(prefillEmail);
  const isPhone = looksLikePhone(identifier);
  const [password, setPassword] = useState("");
  const [signInMethod, setSignInMethod] = useState<SignInMethod>("password");
  const [otpStep, setOtpStep] = useState<OtpStep>("idle");
  const [otpCode, setOtpCode] = useState("");
  const [otpCooldown, setOtpCooldown] = useState(0);

  // Alias for backward compat with signup flow that references `email`
  const email = identifier;
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<string | null>(null);
  const [error, setError] = useState(
    oauthError === "oauth_code_missing"
      ? "Sign-in was cancelled or failed. Please try again."
      : oauthError === "oauth_exchange_failed"
        ? "Could not complete sign-in. Please try again."
        : ""
  );
  const [message, setMessage] = useState(
    linkApple
      ? "Sign in with your existing account, then link Apple from Account settings."
      : ""
  );
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [googleWebClientId, setGoogleWebClientId] = useState<string | null>(null);
  const hasNativeGoogleConfig = !!googleWebClientId;

  useEffect(() => {
    // Fetch Google Web Client ID from product-level app settings
    fetch("/api/auth/google-client-id")
      .then((r) => r.json())
      .then((data) => {
        if (data.webClientId) setGoogleWebClientId(data.webClientId);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    // Skip auto-redirect when adding another account
    if (isAddAccount) return;
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        router.replace("/");
      }
    });
  }, [router, isAddAccount]);

  // OTP cooldown timer
  useEffect(() => {
    if (otpCooldown <= 0) return;
    const timer = setTimeout(() => setOtpCooldown(otpCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [otpCooldown]);

  const handleSendOtp = async () => {
    setError("");
    setMessage("");
    if (!identifier.trim()) {
      setError(t("enterEmailOrPhone"));
      return;
    }
    if (!isPhone) {
      setError(t("otpPhoneOnly"));
      return;
    }
    setLoading(true);
    try {
      console.log("[login] Sending OTP to phone:", identifier);
      const res = await trackedFetch("/api/auth/otp/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: identifier }),
      });
      const data = await res.json();
      if (!res.ok) {
        console.error("[login] OTP send failed:", res.status, data);
        throw new Error(data.error || "Failed to send code");
      }

      console.log("[login] OTP sent successfully");
      setOtpStep("sent");
      setOtpCooldown(60);
      setMessage(t("otpSentPhone"));
    } catch (err) {
      console.error("[login] OTP send error:", err);
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError("");
    setMessage("");
    setLoading(true);
    setOtpStep("verifying");
    try {
      // Clear nav-counts on login
      if (typeof window !== "undefined") {
        localStorage.removeItem("nav-counts");
      }

      // When adding an account, save the current session first
      if (isAddAccount) {
        try {
          await trackedFetch("/api/auth/sessions/add", { method: "POST" });
        } catch {
          // Non-fatal
        }
      }

      console.log("[login] Verifying OTP for phone:", identifier);
      const res = await trackedFetch("/api/auth/otp/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: identifier, code: otpCode }),
      });
      const data = await res.json();
      if (!res.ok) {
        console.error("[login] OTP verify failed:", res.status, data);
        throw new Error(data.error || "Verification failed");
      }
      console.log("[login] OTP verified, session created");

      // Session cookies are set by the API route — now run client-side post-auth
      await runPostAuthProvisioning();
      await saveTokensToNativeStorage();

      // Save session to multi-account store
      try {
        await trackedFetch("/api/auth/sessions/add", { method: "POST" });
      } catch {
        // Non-fatal
      }

      // Check if MFA is required
      try {
        const mfaRes = await fetch("/api/mfa/status");
        if (mfaRes.ok) {
          const mfaData = await mfaRes.json();
          if (mfaData.mfa_required && !mfaData.mfa_verified) {
            await trackedFetch("/api/mfa/challenge", { method: "POST" });
            window.location.href = "/mfa/challenge";
            return;
          }
        }
      } catch {
        // Non-fatal
      }

      window.location.href = isAddAccount ? "/account" : "/";
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      setOtpStep("sent");
    } finally {
      setLoading(false);
    }
  };

  const handleOAuth = async (provider: "apple" | "google") => {
    setError("");
    setMessage("");

    // In sign-up mode, require terms agreement before OAuth
    if (authMode === "signup" && !agreedToTerms) {
      setError(
        "You must agree to the Terms of Service and Privacy Policy to sign up"
      );
      return;
    }

    setOauthLoading(provider);

    try {
      // Clear nav-counts on login
      if (typeof window !== "undefined") {
        localStorage.removeItem("nav-counts");
      }

      // When adding an account, save the current session first
      if (isAddAccount) {
        try {
          await trackedFetch("/api/auth/sessions/add", { method: "POST" });
        } catch {
          // Non-fatal
        }
      }

      // If signing up, store a flag so provisioning records terms agreement
      if (authMode === "signup") {
        localStorage.setItem("pending_terms_agreement", "true");
      }

      // Stash phone number so provisioning can match it after OAuth redirect
      if (phone.trim()) {
        localStorage.setItem("pending_phone_link", phone.trim());
      }
      // Stash IG account ID so provisioning can merge after OAuth redirect
      if (igLinkParam) {
        localStorage.setItem("pending_ig_link", igLinkParam);
      }
      // Stash demo session token so provisioning can claim the demo business
      if (demoTokenParam) {
        localStorage.setItem("pending_demo_token", demoTokenParam);
      }

      // --- NATIVE APPLE SIGN-IN PATH (iOS) ---
      // Falls through to web OAuth if native sign-in fails (e.g., entitlement not yet configured)
      if (provider === "apple" && isNative) {
        try {
          const { SocialLogin } = await import("@capgo/capacitor-social-login");

          await SocialLogin.initialize({
            apple: {},
          });

          const res = await SocialLogin.login({
            provider: "apple",
            options: {
              scopes: ["email", "name"],
            },
          });

          const idToken = res.result && "idToken" in res.result ? res.result.idToken : null;
          if (!idToken) {
            throw new Error("no ID token");
          }

          const { error: supaError } = await supabase.auth.signInWithIdToken({
            provider: "apple",
            token: idToken,
          });

          if (supaError) throw supaError;

          await runPostAuthProvisioning();
          await saveTokensToNativeStorage();

          // Check if MFA is required
          try {
            const mfaRes = await fetch("/api/mfa/status");
            if (mfaRes.ok) {
              const mfaData = await mfaRes.json();
              if (mfaData.mfa_required && !mfaData.mfa_verified) {
                await trackedFetch("/api/mfa/challenge", { method: "POST" });
                window.location.href = "/mfa/challenge";
                return;
              }
            }
          } catch {
            // Non-fatal — proceed without MFA
          }

          window.location.href = isAddAccount ? "/account" : "/";
          return;
        } catch (nativeErr) {
          console.warn("[login] Native Apple sign-in unavailable, falling back to web OAuth:", nativeErr);
          // Fall through to web OAuth redirect below
        }
      }

      // --- NATIVE GOOGLE SIGN-IN PATH ---
      if (provider === "google" && isNative) {
        // Dynamic import to avoid bundling native code on web / SSR issues
        const { SocialLogin } = await import("@capgo/capacitor-social-login");

        await SocialLogin.initialize({
          google: {
            webClientId: googleWebClientId!,
          },
        });

        const res = await SocialLogin.login({
          provider: "google",
          options: {
            scopes: ["email", "profile"],
          },
        });

        const idToken = res.result && "idToken" in res.result ? res.result.idToken : null;
        if (!idToken) {
          throw new Error("Google sign-in failed: no ID token received");
        }

        // Exchange the native ID token with Supabase
        const { error: supaError } = await supabase.auth.signInWithIdToken({
          provider: "google",
          token: idToken,
        });

        if (supaError) throw supaError;

        // Run provisioning (same steps as /auth/callback)
        await runPostAuthProvisioning();

        // Save tokens for Siri/voice assistant access
        await saveTokensToNativeStorage();

        // Check if MFA is required
        try {
          const mfaRes = await fetch("/api/mfa/status");
          if (mfaRes.ok) {
            const mfaData = await mfaRes.json();
            if (mfaData.mfa_required && !mfaData.mfa_verified) {
              await trackedFetch("/api/mfa/challenge", { method: "POST" });
              window.location.href = "/mfa/challenge";
              return;
            }
          }
        } catch {
          // Non-fatal — proceed without MFA
        }

        // Navigate to the appropriate destination
        window.location.href = isAddAccount ? "/account" : "/";
        return;
      }

      // --- WEB OAUTH REDIRECT PATH (unchanged) ---
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/confirm`,
        },
      });

      if (error) throw error;
      // Browser will redirect to the OAuth provider
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      setOauthLoading(null);
    }
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setLoading(true);

    try {
      // Always clear nav-counts from localStorage on login/signup
      if (typeof window !== "undefined") {
        localStorage.removeItem("nav-counts");
      }
      if (authMode === "signup") {
        // Validate agreement checkbox
        if (!agreedToTerms) {
          setError(
            "You must agree to the Terms of Service and Privacy Policy to sign up"
          );
          setLoading(false);
          return;
        }

        // Stash phone number so provisioning can match it after email confirmation
        if (phone.trim()) {
          localStorage.setItem("pending_phone_link", phone.trim());
        }
        // Stash IG account ID so provisioning can merge after email confirmation
        if (igLinkParam) {
          localStorage.setItem("pending_ig_link", igLinkParam);
        }
        // Stash demo session token so provisioning can claim the demo business
        if (demoTokenParam) {
          localStorage.setItem("pending_demo_token", demoTokenParam);
        }

        // Create account
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/auth/callback`,
          },
        });
        if (error) throw error;

        // If email confirmation is disabled, signUp returns a session immediately
        if (data.session) {
          localStorage.setItem("pending_terms_agreement", "true");

          await runPostAuthProvisioning();
          await saveTokensToNativeStorage();

          // Merge any provisional accounts matching phone or IG account ID
          const mergeBody: Record<string, string> = {};
          if (phone.trim()) mergeBody.phone = phone.trim();
          if (igLinkParam) mergeBody.ig_account_id = igLinkParam;
          if (Object.keys(mergeBody).length > 0) {
            try {
              await trackedFetch("/api/auth/provision", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(mergeBody),
              });
            } catch {
              // Non-fatal — merge can be retried
            }
          }

          try {
            await trackedFetch("/api/auth/sessions/add", { method: "POST" });
          } catch {
            // Non-fatal
          }

          window.location.href = "/";
          return;
        }

        // Email confirmation required — store flag for after confirmation
        if (data.user) {
          localStorage.setItem("pending_terms_agreement", "true");
        }

        setMessage("Check your email to confirm your account!");
        setIdentifier("");
        setPassword("");
        setAgreedToTerms(false);
        setAuthMode("signin");
      } else {
        // When adding an account, save the current session BEFORE
        // signInWithPassword replaces it with the new account's session
        if (isAddAccount) {
          try {
            await trackedFetch("/api/auth/sessions/add", { method: "POST" });
          } catch {
            // Non-fatal
          }
        }

        const signInCredentials = isPhone
          ? { phone: identifier, password }
          : { email: identifier, password };
        console.log("[login] Signing in with password, isPhone:", isPhone);
        const { error } = await supabase.auth.signInWithPassword(signInCredentials);
        if (error) {
          console.error("[login] signInWithPassword failed:", error.message, error.status, error);
          throw error;
        }
        console.log("[login] Password sign-in successful");

        // Merge any provisional accounts matching phone or IG account ID
        const mergeBody: Record<string, string> = {};
        if (phone.trim()) mergeBody.phone = phone.trim();
        if (igLinkParam) mergeBody.ig_account_id = igLinkParam;
        if (Object.keys(mergeBody).length > 0) {
          try {
            await trackedFetch("/api/auth/provision", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(mergeBody),
            });
          } catch {
            // Non-fatal — merge can be retried
          }
        }

        // Save the newly signed-in session to multi-account store
        try {
          await trackedFetch("/api/auth/sessions/add", { method: "POST" });
        } catch {
          // Non-fatal
        }

        // Save tokens for Siri/voice assistant access
        await saveTokensToNativeStorage();

        // Check if MFA is required
        try {
          const mfaRes = await fetch("/api/mfa/status");
          if (mfaRes.ok) {
            const mfaData = await mfaRes.json();
            if (mfaData.mfa_required && !mfaData.mfa_verified) {
              await trackedFetch("/api/mfa/challenge", { method: "POST" });
              window.location.href = "/mfa/challenge";
              return;
            }
          }
        } catch {
          // Non-fatal — proceed without MFA
        }

        if (isAddAccount) {
          window.location.href = "/account";
        } else {
          window.location.href = "/";
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      setLoading(false);
    }
  };

  const switchMode = (mode: AuthMode) => {
    setAuthMode(mode);
    setSignInMethod("password");
    setOtpStep("idle");
    setOtpCode("");
    setError("");
    setMessage("");
  };

  const anyLoading = loading || oauthLoading !== null;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
      <div className="w-full max-w-md">
        <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
          {isAddAccount && (
            <a
              href="/account"
              className="inline-flex items-center gap-1 text-gray-500 hover:text-gray-900 text-sm mb-4 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
              {t("backToAccount")}
            </a>
          )}

          <div className="flex items-center gap-3 mb-4">
            {(branding.monogram ?? branding.logo)?.startsWith("/") && (
              <img
                src={branding.monogram ?? branding.logo}
                alt={branding.name}
                className="w-10 h-10 rounded-lg object-contain"
              />
            )}
            <span className="text-2xl font-bold text-gray-900 tracking-tight">{branding.name}</span>
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {isAddAccount
              ? t("addAccount")
              : authMode === "signup"
                ? t("createAccount")
                : t("welcomeBack")}
          </h1>
          <p className="text-gray-500 mb-6">
            {isAddAccount
              ? t("signInWithAnotherAccount")
              : authMode === "signup"
                ? t("signUpToGetStarted")
                : t("signInToYourAccount")}
          </p>

          {error && (
            <Notice tone="danger" className="mb-4">
              {error}
            </Notice>
          )}

          {message && (
            <Notice tone="success" className="mb-4">
              {message}
            </Notice>
          )}

          {/* OAuth buttons — wrapper collapses entirely when both providers are hidden */}
          {((appleOAuthEnabled && !isAndroid && !isIOS) ||
            (googleOAuthEnabled && !isIOS && !(isAndroid && !hasNativeGoogleConfig))) && (
          <div className="space-y-3 mb-6">
            {appleOAuthEnabled && !isAndroid && !isIOS && (
              <button
                type="button"
                onClick={() => handleOAuth("apple")}
                disabled={anyLoading}
                className="w-full flex items-center justify-center gap-3 bg-black hover:bg-gray-900 disabled:bg-gray-300 disabled:text-gray-500 text-white font-semibold py-3 rounded-xl transition"
              >
                <AppleIcon className="w-5 h-5" />
                {oauthLoading === "apple"
                  ? t("redirecting")
                  : authMode === "signup"
                    ? t("signUpWithApple")
                    : t("continueWithApple")}
              </button>
            )}

            {/* Hide Google on iOS (App Store policy) and on Android if native credentials aren't configured */}
            {googleOAuthEnabled && !isIOS && !(isAndroid && !hasNativeGoogleConfig) && (
              <button
                type="button"
                onClick={() => handleOAuth("google")}
                disabled={anyLoading}
                className="w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 text-gray-800 font-semibold py-3 rounded-xl transition border border-gray-300"
              >
                <GoogleIcon className="w-5 h-5" />
                {oauthLoading === "google"
                  ? (isNative ? t("signingIn") : "Redirecting...")
                  : authMode === "signup"
                    ? t("signUpWithGoogle")
                    : t("continueWithGoogle")}
              </button>
            )}
          </div>
          )}

          {/* Divider — only show when at least one OAuth button is visible */}
          {((appleOAuthEnabled && !isAndroid && !isIOS) ||
            (googleOAuthEnabled && !isIOS && !(isAndroid && !hasNativeGoogleConfig))) && (
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-gray-400 text-sm">or</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>
          )}

          {/* OTP verification step */}
          {signInMethod === "otp" && otpStep !== "idle" ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("verificationCode")}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-500 focus:ring-1 focus:ring-gray-500 transition text-center text-2xl tracking-[0.5em] font-mono"
                  placeholder="000000"
                  maxLength={6}
                />
              </div>

              <button
                type="button"
                onClick={handleVerifyOtp}
                disabled={anyLoading || otpCode.length !== 6}
                className="w-full bg-gray-900 hover:bg-gray-800 disabled:bg-gray-300 disabled:text-gray-500 text-white font-semibold py-3 rounded-xl transition"
              >
                {otpStep === "verifying" ? t("verifyingCode") : t("verifyAndSignIn")}
              </button>

              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => {
                    setOtpStep("idle");
                    setOtpCode("");
                    setMessage("");
                  }}
                  className="text-sm text-gray-500 hover:text-gray-900 transition-colors"
                >
                  {t("backToLogin")}
                </button>
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={otpCooldown > 0 || anyLoading}
                  className="text-sm text-gray-500 hover:text-gray-900 transition-colors disabled:text-gray-300"
                >
                  {otpCooldown > 0
                    ? `${t("resendCode")} (${otpCooldown}s)`
                    : t("resendCode")}
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={signInMethod === "otp" ? (e) => { e.preventDefault(); handleSendOtp(); } : handleAuth} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("emailOrPhone")}
                </label>
                <input
                  type={isPhone ? "tel" : "email"}
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  required
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-500 focus:ring-1 focus:ring-gray-500 transition"
                  placeholder={t("emailOrPhonePlaceholder")}
                />
              </div>

              {signInMethod === "password" && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {t("password")}
                    </label>
                    {authMode !== "signup" && (
                      <a
                        href="/forgot-password"
                        className="text-sm text-gray-500 hover:text-gray-900 transition-colors"
                      >
                        {t("forgotPassword")}
                      </a>
                    )}
                  </div>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-500 focus:ring-1 focus:ring-gray-500 transition"
                    placeholder="••••••••"
                  />
                </div>
              )}

              {/* Phone number field — shown on signup with password method, pre-filled from ?phone= param */}
              {authMode === "signup" && signInMethod === "password" && !isPhone && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("phoneOptional")}
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-500 focus:ring-1 focus:ring-gray-500 transition"
                    placeholder="(555) 123-4567"
                  />
                </div>
              )}

              {authMode === "signup" && (
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="terms-agreement"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    required
                    className="mt-1 w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-500"
                  />
                  <label
                    htmlFor="terms-agreement"
                    className="text-sm text-gray-600"
                  >
                    {t("byCreatingAnAccountYouAgreeToThe")}{" "}
                    <a
                      href="/terms"
                      className="text-gray-900 font-medium underline hover:text-gray-600"
                    >
                      {t("termsOfService")}
                    </a>{" "}
                    {t("and")}{" "}
                    <a
                      href="/privacy"
                      className="text-gray-900 font-medium underline hover:text-gray-600"
                    >
                      {t("privacyPolicy")}
                    </a>
                    .
                  </label>
                </div>
              )}

              <button
                type="submit"
                disabled={anyLoading}
                className="w-full bg-gray-900 hover:bg-gray-800 disabled:bg-gray-300 disabled:text-gray-500 text-white font-semibold py-3 rounded-xl transition"
              >
                {loading
                  ? signInMethod === "otp"
                    ? t("sendingCode")
                    : authMode === "signup"
                      ? t("signingUp")
                      : t("signingIn")
                  : signInMethod === "otp"
                    ? t("sendOneTimeCode")
                    : authMode === "signup"
                      ? t("signUpWithEmail")
                      : t("signIn")}
              </button>

              {/* Toggle between password and OTP */}
              {SHOW_OTP_SIGN_IN && authMode !== "signup" && (
                <button
                  type="button"
                  onClick={() => {
                    setSignInMethod(signInMethod === "password" ? "otp" : "password");
                    setError("");
                    setMessage("");
                  }}
                  className="w-full text-sm text-gray-500 hover:text-gray-900 transition-colors py-1"
                >
                  {signInMethod === "password"
                    ? t("useOneTimeCode")
                    : t("usePassword")}
                </button>
              )}
            </form>
          )}

          {/* Mode toggles */}
          {allowSignUp && (
            <div className="mt-6 text-center text-gray-500">
              {authMode === "signup" ? (
                <>
                  {t("alreadyHaveAnAccount")}{" "}
                  <button
                    onClick={() => switchMode("signin")}
                    className="text-gray-900 font-medium hover:underline"
                  >
                    {t("signIn")}
                  </button>
                </>
              ) : (
                <>
                  {t("dontHaveAnAccount")}{" "}
                  <button
                    onClick={() => switchMode("signup")}
                    className="text-gray-900 font-medium hover:underline"
                  >
                    {t("signUp")}
                  </button>
                </>
              )}
            </div>
          )}

          <div className="mt-6 pt-4 border-t border-gray-200 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-gray-400">
            <a
              href="/terms"
              className="hover:text-gray-900 transition-colors"
            >
              Terms of Service
            </a>
            <span>·</span>
            <a
              href="/privacy"
              className="hover:text-gray-900 transition-colors"
            >
              Privacy Policy
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
